import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Alert, 
  Typography, 
  Space,
  Result,
  Descriptions,
  message,
  Row,
  Col,
  Modal,
  Tag
} from 'antd';
import { 

  CopyOutlined,

} from '@ant-design/icons';
import { useAccount } from '../../context/AccountContext';
import { formatAmount } from '../../utils';

const { Text } = Typography;

interface RechargeAccountInfo {
  id: string;
  accountNo: string;
  accountNumber: string;
  accountName: string;
  bind_acct_bank_nme: string;
  bankCode: string;
  bankBranch: string;
  purpose: string;
}

const Recharge: React.FC = () => {
  const { accountStatus, updateAccountStatus } = useAccount();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  
  // 重置充值状态
  useEffect(() => {
    setCurrentStep(0);
    setRechargeAmount('');
  }, []);
  const [loading, setLoading] = useState(false);
  const [rechargeInfo, setRechargeInfo] = useState<RechargeAccountInfo | null>(null);
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [showDemoControl, setShowDemoControl] = useState(false);
  const [demoStatus, setDemoStatus] = useState<'found' | 'not_found'>('found');
  const [systemSource, setSystemSource] = useState<'upstream' | 'settlement'>('settlement');

  // 步骤1：输入充值金额
  const handleAmountSubmit = async (values: any) => {
    setLoading(true);
    try {
      // 模拟获取充值账户信息
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const mockRechargeInfo: RechargeAccountInfo = {
        id: '1',
        accountNo: '310069906618170001234',
        accountName: '上海数据交易所有限公司',
        accountNumber: '310069906618170001234',
        bind_acct_bank_nme: '交通银行',
        bankCode: '301290000007',
        bankBranch: '交通银行上海分行营业部',
        purpose: `充值-${Date.now()}`
      };

      setRechargeInfo(mockRechargeInfo);
      setRechargeAmount(values.amount);
      setCurrentStep(1);
    } catch (error) {
      message.error('获取充值信息失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 步骤2：完成转账后确认
  const handleConfirmTransfer = async () => {
    if (demoStatus === 'not_found') {
      // 未查询到流水时弹框提示
      Modal.confirm({
        title: '未查询到流水记录',
        content: (
          <div>
            <p>系统未查询到您的充值流水记录，请确认：</p>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>充值金额是否正确</li>
              <li>转账附言是否填写正确</li>
              <li>银行转账是否已成功</li>
            </ul>
            <p>如果确认信息无误，请耐心等待系统处理</p>
          </div>
        ),
        okText: '我知道了',
        cancelButtonProps: { style: { display: 'none' } },
        onOk: () => {
          message.info('请耐心等待，系统会继续查询您的充值记录');
        }
      });
      return;
    }

    setLoading(true);
    try {
      // 模拟确认转账并同步余额
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      if (demoStatus === 'found') {
        // 更新账户余额（模拟）
        const currentBalance = parseFloat(accountStatus.balance);
        const newBalance = currentBalance + parseFloat(rechargeAmount);
        
        updateAccountStatus({
          balance: newBalance.toFixed(2)
        });

        setCurrentStep(2);
        message.success('充值成功！余额已更新');
      }
    } catch (error) {
      message.error('充值确认失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 演示模式状态切换
  const handleDemoStatusChange = (status: 'found' | 'not_found') => {
    setDemoStatus(status);
    setShowDemoControl(false);
  };

  // 复制到剪贴板
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`${label}已复制到剪贴板`);
    }).catch(() => {
      message.error('复制失败');
    });
  };


  // 步骤定义（已移除导航条，仅保留注释）
  // 步骤1：充值申请 - 输入充值金额
  // 步骤2：银行转账 - 向指定账户转账  
  // 步骤3：充值完成 - 确认到账

  // 渲染步骤1：充值申请
  const renderStep1 = () => (
    <Card title="充值申请" className="step-card">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleAmountSubmit}
        requiredMark={false}
      >
        <Alert
          message="充值说明"
          description={
            <div>
              <p>• 请通过网银或手机银行向我们提供的专用账户转账</p>
              <p>• 转账时请务必填写正确的附言信息，以便系统识别您的充值</p>

            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Row>
          <Col span={12}>
            <Form.Item 
              name="amount" 
              label="充值金额（元）" 
              rules={[
                { required: true, message: '请输入充值金额' },
                { 
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const amount = parseFloat(value);
                    if (isNaN(amount) || amount <= 0) {
                      return Promise.reject(new Error('请输入有效的金额'));
                    }
                    if (amount < 100) {
                      return Promise.reject(new Error('充值金额不能少于100元'));
                    }
                    if (amount > 1000000) {
                      return Promise.reject(new Error('充值金额不能超过100万元'));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Input 
                size="large"
                placeholder="请输入充值金额" 
                prefix="¥"
                style={{ fontSize: '16px' }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item style={{ textAlign: 'center' }}>
          <Button 
            type="primary" 
            htmlType="submit" 
            size="large" 
            loading={loading}
            style={{ minWidth: 120 }}
          >
            开始充值
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );

  // 渲染步骤2：银行转账
  const renderStep2 = () => (
    <Card title="银行转账" className="step-card" style={{ position: 'relative' }}>
      {/* 状态浮标 */}
      <div style={{
        position: 'fixed',
        top: '120px',
        right: '20px',
        zIndex: 1000,
        background: 'rgba(255, 255, 255, 0.9)',
        padding: '12px 16px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        border: '1px solid #d9d9d9',
        minWidth: '140px'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '8px' }}>
          <Text strong style={{ fontSize: '14px' }}>流水查询状态</Text>
        </div>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button 
            size="small"
            type={demoStatus === 'found' ? 'primary' : 'default'}
            onClick={() => handleDemoStatusChange('found')}
            block
          >
            查询到流水
          </Button>
          <Button 
            size="small"
            type={demoStatus === 'not_found' ? 'primary' : 'default'}
            onClick={() => handleDemoStatusChange('not_found')}
            block
          >
            未查询到流水
          </Button>
        </Space>
      </div>

      <Alert
        message="请完成银行转账"
        description="请根据以下信息通过网银或手机银行完成转账，转账完成后点击确认转账完成按钮"
        type="warning"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* 演示模式控制按钮 */}
      <div style={{ position: 'absolute', top: 16, right: 16 }}>
        <Button 
          size="small" 
          type="link"
          onClick={() => setShowDemoControl(!showDemoControl)}
          style={{ color: '#1890ff' }}
        >
          演示模式
        </Button>
      </div>

      <Descriptions 
        bordered 
        column={1} 
        size="middle"
        labelStyle={{ width: '120px', fontWeight: 'bold' }}
      >
        <Descriptions.Item label="收款户名">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={{ fontSize: '16px', fontWeight: 500 }}>
              {rechargeInfo?.accountName}
            </Text>
            <Button 
              type="link" 
              icon={<CopyOutlined />} 
              onClick={() => copyToClipboard(rechargeInfo!.accountName, '收款户名')}
            >
              复制
            </Button>
          </div>
        </Descriptions.Item>
        
        <Descriptions.Item label="收款账号">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={{ fontSize: '16px', fontWeight: 500, fontFamily: 'monospace' }}>
              {rechargeInfo?.accountNumber}
            </Text>
            <Button 
              type="link" 
              icon={<CopyOutlined />} 
              onClick={() => copyToClipboard(rechargeInfo!.accountNumber, '收款账号')}
            >
              复制
            </Button>
          </div>
        </Descriptions.Item>
        
        <Descriptions.Item label="收款银行">
          <Text style={{ fontSize: '16px' }}>
            {rechargeInfo?.bankBranch}
          </Text>
        </Descriptions.Item>
        
        <Descriptions.Item label="转账金额">
          <Text style={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}>
            ¥{formatAmount(rechargeAmount)}
          </Text>
        </Descriptions.Item>
        
        <Descriptions.Item label="转账附言">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={{ fontSize: '16px', fontWeight: 500, color: '#fa8c16' }}>
              {rechargeInfo?.purpose}
            </Text>
            <Button 
              type="link" 
              icon={<CopyOutlined />} 
              onClick={() => copyToClipboard(rechargeInfo!.purpose, '转账附言')}
            >
              复制
            </Button>
          </div>
        </Descriptions.Item>
        

      </Descriptions>

      <Alert
        message="重要提醒"
        description="请务必在转账时填写正确的转账附言，否则可能导致无法及时到账。如有疑问请联系客服。"
        type="error"
        showIcon
        style={{ margin: '24px 0' }}
      />

      <div style={{ textAlign: 'center' }}>
        <Space size="large">
          <Button 
            size="large"
            onClick={() => setCurrentStep(0)}
          >
            返回修改金额
          </Button>
          <Button 
            type="primary" 
            size="large" 
            loading={loading}
            onClick={handleConfirmTransfer}
            style={{ minWidth: 140 }}
          >
            {loading ? '处理中...' : '确认已完成转账'}
          </Button>
        </Space>
      </div>
    </Card>
  );

  // 渲染步骤3：充值完成
  const renderStep3 = () => {
    
    
    const handleContinuePayment = () => {
      // 打开空白页面
      window.open('about:blank', '_blank');
      message.info('已打开继续支付页面');
    };

    const handleViewTransactions = () => {
      window.location.href = '/bill-query';
    };

    return (
      <Card className="step-card">
        {/* 系统来源悬浮选择框 */}
        <div style={{
          position: 'fixed',
          top: 120,
          right: 20,
          zIndex: 1000,
          background: 'rgba(255, 255, 255, 0.9)',
          padding: '12px 16px',
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          border: '1px solid #d9d9d9',
          minWidth: 220
        }}>
          <div style={{ textAlign: 'center', marginBottom: 8 }}>
            <Text strong style={{ fontSize: 14 }}>系统来源</Text>
          </div>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div
              style={{
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                padding: '10px 12px',
                cursor: 'pointer',
                backgroundColor: systemSource === 'upstream' ? '#fafafa' : '#fff',
                textAlign: 'center'
              }}
              onClick={() => setSystemSource('upstream')}
            >
              <Tag color="blue" style={{ fontSize: 12, marginBottom: 6 }}>
                上游系统来源
              </Tag>
              <div style={{ fontSize: 12, color: '#666' }}>
                适用于需要继续完成支付流程
              </div>
            </div>

            <div
              style={{
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                padding: '10px 12px',
                cursor: 'pointer',
                backgroundColor: systemSource === 'settlement' ? '#fafafa' : '#fff',
                textAlign: 'center'
              }}
              onClick={() => setSystemSource('settlement')}
            >
              <Tag color="green" style={{ fontSize: 12, marginBottom: 6 }}>
                清结算系统来源
              </Tag>
              <div style={{ fontSize: 12, color: '#666' }}>
                适用于已完成支付的场景
              </div>
            </div>
          </Space>
        </div>
        
        {/* 根据选择的系统来源显示不同按钮 */}
        <Result
          status="success"
          title="充值成功！"
          subTitle={`充值金额 ¥${formatAmount(rechargeAmount)} 已到账，您的账户余额已更新`}
          extra={[
            systemSource === 'upstream' ? (
              <Button type="primary" size="large" key="continue" onClick={handleContinuePayment}>
                返回继续支付
              </Button>
            ) : (
              <Button type="primary" size="large" key="transactions" onClick={handleViewTransactions}>
                查看交易流水
              </Button>
            )
          ]}
        />
      </Card>
    );
  };

  // 渲染步骤4：充值中
  const renderStep4 = () => (
    <Card className="step-card">
      <Result
        status="info"
        title="充值处理中"
        subTitle={`充值金额 ¥${formatAmount(rechargeAmount)} 已提交，请等待系统确认`}
        extra={[
          <Button type="primary" key="recharge" onClick={() => setCurrentStep(0)}>
            再次充值
          </Button>
        ]}
      />
    </Card>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return renderStep1();
      case 1:
        return renderStep2();
      case 2:
        return renderStep3();
      case 3:
        return renderStep4();
      default:
        return null;
    }
  };

  return (
    <div className="page-container">
      <div style={{ maxWidth: 800, margin: '16px auto' }}>




        {renderCurrentStep()}
      </div>
    </div>
  );
};

export default Recharge;